import { Assignment as AssignmentIcon, ExpandLess, ExpandMore, Settings as SettingsIcon } from "@mui/icons-material";
import {
  Box,
  Collapse,
  Divider,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import { useLocation, useNavigate } from "@tanstack/react-router";
import React, { useState } from "react";
import { useUser } from "@/contexts/UserContext";

interface SidebarProps {
  open: boolean;
  onClose: () => void;
  width: number;
  isMobile: boolean;
}

interface MenuItem {
  id: string;
  text: string;
  icon: React.ReactNode;
  path?: string;
  subItems?: { text: string; path: string }[];
  show: boolean;
}

const Sidebar: React.FC<SidebarProps> = ({ open, onClose, width, isMobile }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [openMenus, setOpenMenus] = useState<{ [key: string]: boolean }>({});
  const { isAdmin, isApprover, isManager } = useUser();

  const handleMenuClick = (path: string) => {
    navigate({ to: path });
    if (isMobile) {
      onClose();
    }
  };

  const handleSubMenuClick = (menuId: string) => {
    setOpenMenus((prev) => ({
      ...prev,
      [menuId]: !prev[menuId],
    }));
  };

  const menuItems: MenuItem[] = [
    {
      id: "instruction-statistics",
      text: "지시사항 통계",
      icon: <AssignmentIcon />,
      path: "/manager/instructions/statistics",
      show: isManager,
    },
    {
      id: "instruction",
      text: "지시/방침사항 관리",
      icon: <AssignmentIcon />,
      path: "/instructions",
      show: isManager,
    },
    {
      id: "approval",
      text: "지시/방침사항 관리",
      icon: <AssignmentIcon />,
      subItems: [
        { text: "지시사항 통계", path: "/approver/instructions/statistics" },
        { text: "지시/방침 관리", path: "/instructions" },
      ],
      show: isApprover || isAdmin,
    },
    {
      id: "management",
      text: "관리",
      icon: <SettingsIcon />,
      subItems: [
        { text: "사용자 관리", path: "/approver/users" },
        { text: "부서 관리", path: "/approver/departments" },
        { text: "조직장 확인", path: "/approver/department-heads" },
      ],
      show: isApprover || isAdmin,
    },
  ];

  // 메뉴 오픈 상태를 주소에 맞게 초기화
  React.useEffect(() => {
    const newOpenMenus: { [key: string]: boolean } = {};
    menuItems.forEach((item) => {
      if (item.subItems) {
        if (item.subItems.some((sub) => location.pathname.includes(sub.path))) {
          newOpenMenus[item.id] = true;
        }
      }
    });
    setOpenMenus((prev) => ({ ...prev, ...newOpenMenus }));
  }, [location.pathname]);

  return (
    <Drawer
      variant={!isMobile ? "permanent" : "temporary"}
      open={open}
      onClose={onClose}
      ModalProps={{
        keepMounted: true,
      }}
      sx={{
        width: width,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: width,
          boxSizing: "border-box",
          backgroundColor: "#313131",
          color: "#ffffff",
          borderRight: "none",
          zIndex: (theme) => theme.zIndex.appBar + 100,
        },
      }}
    >
      <Box
        sx={{ height: 64, p: 2, display: "flex", alignItems: "center", cursor: "pointer" }}
        onClick={() => navigate({ to: "/" })}
      >
        {/* 회사 로고 */}
        <Box component='img' src='/images/logo.png' alt='회사 로고' sx={{ height: 32, width: 32, mr: 1 }} />
        <Typography
          variant='h6'
          noWrap
          component='div'
          sx={{
            fontWeight: 600,
            fontSize: "1.25rem",
            color: "#fff",
          }}
        >
          대주·KC DEMS
        </Typography>
      </Box>
      <Divider
        sx={{
          borderColor: "rgba(255, 255, 255, 0.12)",
        }}
      />
      <Box sx={{ px: 2, py: 1 }}>
        <List sx={{ px: 2, pt: 2 }}>
          {menuItems.map(
            (item) =>
              item.show && (
                <React.Fragment key={item.id}>
                  {item.subItems ? (
                    <>
                      <ListItemButton
                        onClick={() => handleSubMenuClick(item.id)}
                        sx={{
                          borderRadius: 1,
                          mb: 0.5,
                          "&:hover": {
                            backgroundColor: "rgba(255, 255, 255, 0.08)",
                          },
                        }}
                      >
                        <ListItemIcon sx={{ color: "rgba(255, 255, 255, 0.7)", minWidth: 40 }}>
                          {item.icon}
                        </ListItemIcon>
                        <ListItemText
                          primary={item.text}
                          sx={{
                            "& .MuiTypography-root": {
                              fontSize: "0.875rem",
                              fontWeight: 500,
                            },
                          }}
                        />
                        {openMenus[item.id] ? <ExpandLess /> : <ExpandMore />}
                      </ListItemButton>
                      <Collapse in={openMenus[item.id]} timeout='auto' unmountOnExit>
                        <List component='div' disablePadding>
                          {item.subItems.map((subItem) => (
                            <ListItemButton
                              key={subItem.path}
                              onClick={() => handleMenuClick(subItem.path)}
                              sx={{
                                pl: 5,
                                borderRadius: 1,
                                mb: 0.5,
                                "&:hover": {
                                  backgroundColor: "rgba(255, 255, 255, 0.08)",
                                },
                                backgroundColor:
                                  location.pathname === subItem.path ? "rgba(255, 255, 255, 0.08)" : "transparent",
                              }}
                            >
                              <ListItemText
                                primary={subItem.text}
                                sx={{
                                  "& .MuiTypography-root": {
                                    fontSize: "0.875rem",
                                    fontWeight: 500,
                                    color: "rgba(255, 255, 255, 0.7)",
                                  },
                                }}
                              />
                            </ListItemButton>
                          ))}
                        </List>
                      </Collapse>
                    </>
                  ) : (
                    <ListItemButton
                      onClick={() => item.path && handleMenuClick(item.path)}
                      sx={{
                        borderRadius: 1,
                        mb: 0.5,
                        "&:hover": {
                          backgroundColor: "rgba(255, 255, 255, 0.08)",
                        },
                        backgroundColor: location.pathname === item.path ? "rgba(255, 255, 255, 0.08)" : "transparent",
                      }}
                    >
                      <ListItemIcon sx={{ color: "rgba(255, 255, 255, 0.7)", minWidth: 40 }}>{item.icon}</ListItemIcon>
                      <ListItemText
                        primary={item.text}
                        sx={{
                          "& .MuiTypography-root": {
                            fontSize: "0.875rem",
                            fontWeight: 500,
                          },
                        }}
                      />
                    </ListItemButton>
                  )}
                </React.Fragment>
              )
          )}
        </List>
      </Box>
    </Drawer>
  );
};

export default Sidebar;
